import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { CardPackageService } from '../../cards-packages/services';
import { BusinessDetails } from '@App/common/models';
import {
  AnalyticsOptionsEnum,
  AnalyticsTypesEnum,
  EmployerActionsEnum,
  IconStatesEnum,
  PitchCardTypeEnum
} from '@App/common/enums';
import { StorageService } from '@Shared/services';
import { RestApiService, UserCommonApiService } from '@App/common/services';
import { AppSettings } from '@Shared/app.settings';
import {
  AnalyticsTabInterface,
  OrganizationProgress
} from '@App/common/interfaces';

export type PitchCardTypeString =
  | 'job'
  | 'resume'
  | 'basic'
  | 'employee'
  | 'service';

export interface PitchCardTypeParams {
  color: string;
  label: string;
  option: PitchCardTypeString;
  value: number;
  count: number | null;
  optionDisabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class EmployerPortalService {
  // Employer portal Subscriptions
  public $adminsModalsHandler: Subject<any> = new Subject<any>();
  public $choosePcTypeModalsHandler: Subject<any> = new Subject<any>();
  public $portalAnalyticsModalHandler: Subject<any> = new Subject<any>();
  public $portalBillingModalHandler: Subject<any> = new Subject<any>();
  public $editEpModalHandler: Subject<any> = new Subject<any>();
  public $applicantsModalHandler: Subject<any> = new Subject<any>();

  // Businesses Subscriptions
  public $previewModalsHandler: Subject<BusinessDetails> =
    new Subject<BusinessDetails>();
  public $updateBusinesses: Subject<PitchCardTypeEnum> =
    new Subject<PitchCardTypeEnum>();
  public $businessAnalyticsModalHandler: Subject<BusinessDetails> =
    new Subject<BusinessDetails>();
  public $businessTestimonialsModalHandler: Subject<boolean> =
    new Subject<boolean>();
  public $businessSearch: Subject<{ event: string; param: string }> =
    new Subject<{ event: string; param: string }>();
  public $fetchEmployerPortalInfo: Subject<boolean> = new Subject<boolean>();
  public $autofillJobCard: Subject<any> = new Subject<any>();
  public $paymentFailedModal: Subject<any> = new Subject<any>();

  public $shutDownAllModal: Subject<any> = new Subject<any>();

  activeBusinessType: {
    color: string;
    label: string;
    option: string;
    value: number;
    count: number;
    optionDisabled: boolean;
  };
  organizationProgress: number;
  businessesLoading = false;

  private _currentBusiness: BusinessDetails = null;

  constructor(
    private restApiService: RestApiService,
    private cardPackageService: CardPackageService,
    private storageService: StorageService,
    private userService: UserCommonApiService
  ) {}

  getEmployerPortalById(organizationId?: string | number): Observable<any> {
    return organizationId
      ? (this.restApiService.get(
          'get-employer-portal-by-id',
          `organization/${organizationId}`,
          true
        ) as Observable<any>)
      : (this.restApiService.get(
          'get-employer-portal',
          'organization/list',
          true
        ) as Observable<any>);
    // TODO: Mock - this.employerPortalMock() as Observable<any>;
  }

  createEmployerPortal(params: any): Observable<any> {
    return this.restApiService.post(
      'create-employer-portal',
      'organization',
      params
    );
  }

  getEmployerPortalBusinesses(
    businessType: string,
    searchText: string,
    orderBy: string,
    desc: boolean,
    limit: number,
    skip: number
  ): Observable<any> {
    return this.restApiService.get(
      'organization-businesses',
      'organization/get-organization-business',
      null,
      {
        businessType,
        searchText,
        orderBy,
        desc,
        limit,
        skip
      }
    );
  }

  getUpdateBusiness(businessType: string, businessId: any): Observable<any> {
    return this.restApiService.get(
      'organization-business',
      'organization/get-organization-business',
      false,
      {
        businessType,
        businessId,
        limit: 1
      }
    );
  }

  changeLockCard(orgId: string | number): Observable<any> {
    return this.restApiService.put(
      'organization-business',
      `organization/${orgId}/change-lock-pitchcard`
    );
  }

  changeAutofillOption(orgId: string | number): Observable<any> {
    return this.restApiService.put(
      'organization-business',
      `organization/${orgId}/change-allow-autofill`
    );
  }

  addAdminsToOrganization(
    organizationId: number | string,
    params: any[]
  ): Observable<any> {
    return this.restApiService.post(
      'add-organization-admins',
      `organization/${organizationId}/add-admin`,
      params,
      true
    );
  }

  removeAdminsFromOrganization(
    organizationId: number | string,
    params: any[]
  ): Observable<any> {
    return this.restApiService.post(
      'delete-admins-from-portal',
      `organization/${organizationId}/remove-admins`,
      params,
      null
    );
  }

  deleteInvitedBusinessesFromPortal(businessIds: string[]): Observable<any> {
    return this.restApiService.delete(
      'delete-business-from-portal',
      'organization/business',
      null,
      businessIds
    );
  }

  getEmployerPortalInviteInfo(inviteId: any): Observable<any> {
    return this.restApiService.get(
      'get-employer-portal-info',
      `organization/invite/${inviteId}`,
      null
    );
  }

  attachPitchCard(inviteId: string, businessId: string) {
    return this.restApiService.post(
      'attach-pitch-card',
      'organization/business-by-invite',
      [businessId],
      false,
      {
        inviteId
      }
    );
  }

  updateOrganizationByIndividualParameters(
    organizationId: number | string,
    params: any
  ): Observable<any> {
    return this.restApiService.put(
      'update-organization-tile',
      `organization/${organizationId}/tile`,
      params,
      true
    );
  }

  addCardDetail(organizationId: number | string, params: any): Observable<any> {
    return this.restApiService.post(
      'add-organization-card',
      `organization/${organizationId}/add-card-full`,
      params,
      null
    );
  }

  inviteUserByEmail(
    organizationId: number | string,
    params: { email: string; businessId: number | string }
  ): Observable<any> {
    return this.restApiService.post(
      'invite-user-to-organization',
      `organization/${organizationId}/invite`,
      params,
      null
    );
  }

  updateUserEmail(
    organizationId: number | string,
    params: { email: string; businessId: number | string }
  ): Observable<any> {
    return this.restApiService.post(
      'update-user-email',
      `organization/${organizationId}/${params.businessId}/update-user-member?email=${params.email}`,
      params,
      null
    );
  }

  // TODO: check actuality
  calculateOrganizationPlan(data: any): Observable<any> {
    return this.restApiService.post(
      'get-organization-plan',
      'organization/calculate-plan',
      data,
      true
    );
  }

  setTestimonialApproveMode(orgId: string): Observable<any> {
    return this.restApiService.put(
      'set-testimonial-approve-mode',
      `organization/${orgId}/change-auto-approve-testimonials`,
      true
    );
  }

  joinTeamByInvite(inviteId: string | number): Observable<any> {
    return this.restApiService.post(
      'join-team-by-invite',
      `organization/invite/${inviteId}/join`,
      null
    );
  }

  initPitchCardTypeDropdown(): PitchCardTypeParams[] {
    const types: PitchCardTypeEnum[] = [
      PitchCardTypeEnum.Job,
      PitchCardTypeEnum.Basic,
      PitchCardTypeEnum.Employee,
      PitchCardTypeEnum.Service
    ];

    const pitchCardTypes: any[] = [];
    types.map((type, index) => {
      pitchCardTypes.push({
        option: type,
        label: type,
        value: index,
        color: ''
      });
      pitchCardTypes[index].label =
        type !== PitchCardTypeEnum.Job
          ? this.getBusinessTypeValue(type) + ' PitchCard'
          : this.getBusinessTypeValue(type);

      if (type === PitchCardTypeEnum.Service) {
        pitchCardTypes[index].color = '#F7CE37';
      }
      if (
        type === PitchCardTypeEnum.Basic ||
        type === PitchCardTypeEnum.Employee
      ) {
        pitchCardTypes[index].color = '#25AFB5';
      }
      if (type === PitchCardTypeEnum.Resume) {
        pitchCardTypes[index].color = '#D52C2C';
      }
      if (type === PitchCardTypeEnum.Job) {
        pitchCardTypes[index].color = '#28b256';
      }
    });

    return pitchCardTypes;
  }

  getBusinessTypeValue(type: string) {
    return type ? this.cardPackageService.getBusinessTypeValue(type) : '';
  }

  calculateOrderPitchCardTypes(
    businessCountConfig: any,
    pitchCardTypes: any[]
  ) {
    if (pitchCardTypes?.length && businessCountConfig) {
      pitchCardTypes.map((card) => {
        card.count = businessCountConfig[card.option];
      });

      return pitchCardTypes.sort((a, b) =>
        a.count < b.count || a.count === b.count ? 1 : -1
      );
    }
  }

  getEPActions(showAdditionalActions: boolean, isAvailable: boolean) {
    return [
      {
        src: '../../../../../../assets/images/my-account-images/team-page/team-edit-default.svg',
        title: 'Edit',
        hide: false,
        id: EmployerActionsEnum.EditEP
      },
      {
        src: '../../../../../../assets/images/my-account-images/team-page/statements.svg',
        title: 'Billing',
        hide: false,
        id: EmployerActionsEnum.Billing
      },
      // { src: '../../../../../../assets/images/my-account-images/team-page/reports.svg', title: 'Reports', hide: false, id: EmployerActionsIds.ReportEP },
      {
        src: '../../../../../../assets/images/my-account-images/team-page/admin-user.svg',
        title: 'Admins',
        hide: false,
        id: EmployerActionsEnum.Admins
      },
      // { src: '', title: 'Add PCs', hide: showAdditionalActions, id: EmployerActionsIds.AddNewPCs },
      {
        src: '',
        title: 'Resume',
        hide: showAdditionalActions || isAvailable,
        id: EmployerActionsEnum.SearchResume
      }
    ];
  }

  getIconActions() {
    return [
      {
        src: '../../../../../assets/images/my-account-images/organization-images/preview-',
        iconId: EmployerActionsEnum.Preview,
        title: 'Preview',
        state: IconStatesEnum.Default,
        hide: false,
        field: '',
        header: '',
        sort: false,
        fixWidth: '40%',
        resize: true
      },
      {
        src: '../../../../../assets/images/my-account-images/organization-images/copy-link-',
        iconId: EmployerActionsEnum.Copy,
        title: 'Copy Link',
        state: IconStatesEnum.Default,
        hide: false,
        field: '',
        header: '',
        sort: false,
        fixWidth: '40%',
        resize: true
      },
      {
        src: '../../../../../assets/images/my-account-images/organization-images/analytics-',
        iconId: EmployerActionsEnum.BusinessReport,
        title: 'Analytics',
        state: IconStatesEnum.Default,
        hide: false,
        field: '',
        header: '',
        sort: false,
        fixWidth: '40%',
        resize: true
      },
      {
        src: '../../../../../assets/images/my-account-images/organization-images/edit-',
        iconId: EmployerActionsEnum.EditBusiness,
        title: 'Edit',
        state: IconStatesEnum.Default,
        hide: false,
        field: '',
        header: '',
        sort: false,
        fixWidth: '40%',
        resize: true
      }
    ];
  }

  chartsAllTabsData(): AnalyticsTabInterface[] {
    return [
      {
        name: AnalyticsTypesEnum.Shared,
        active: true,
        columns: [
          {
            name: 'Quick Share',
            types: [AnalyticsOptionsEnum.ShareQuickShare]
          },
          {
            name: 'Text',
            types: [AnalyticsOptionsEnum.ShareText]
          },
          {
            name: 'Social Media',
            types: [
              AnalyticsOptionsEnum.ShareFacebook,
              AnalyticsOptionsEnum.ShareLinkedin,
              AnalyticsOptionsEnum.ShareTwitter,
              AnalyticsOptionsEnum.ShareInstagram,
              AnalyticsOptionsEnum.SharePinterest
            ]
          },
          {
            name: 'Email',
            types: [AnalyticsOptionsEnum.ShareEmail]
          },
          {
            name: 'Other',
            types: [AnalyticsOptionsEnum.ShareQRCode]
          }
        ]
      },
      {
        name: AnalyticsTypesEnum.Contacted,
        active: false,
        columns: [
          {
            name: 'Call',
            types: [AnalyticsOptionsEnum.ContactCall]
          },
          {
            name: 'Email',
            types: [AnalyticsOptionsEnum.ContactEmail]
          },
          {
            name: 'Text',
            types: [AnalyticsOptionsEnum.ContactChoosen]
          },
          {
            name: 'Schedule',
            types: [AnalyticsOptionsEnum.ContactSheduled]
          },
          {
            name: 'Video Review',
            types: [AnalyticsOptionsEnum.ContactDirections]
          }
        ]
      },
      {
        name: AnalyticsTypesEnum.VideoViewed,
        active: false,
        columns: [
          {
            name: '0-10',
            types: [AnalyticsOptionsEnum.Watched0Plus]
          },
          {
            name: '11-30',
            types: [AnalyticsOptionsEnum.Watched15Plus]
          },
          {
            name: '30-45',
            types: [AnalyticsOptionsEnum.Watched30Plus]
          },
          {
            name: '45+',
            types: [AnalyticsOptionsEnum.Watched45Plus]
          }
        ]
      },
      {
        name: AnalyticsTypesEnum.Pocketed,
        active: false,
        columns: [
          {
            name: 'Added to Pocket',
            types: [AnalyticsOptionsEnum.PocketAdded]
          },
          {
            name: 'Removed from Pocket',
            types: [AnalyticsOptionsEnum.PocketRemoved]
          }
        ]
      }
    ];
  }

  setCurrentBusiness(business: BusinessDetails): void {
    this._currentBusiness = { ...business };
  }

  getCurrentBusiness(): BusinessDetails {
    return { ...this._currentBusiness };
  }

  updateOrganizationProgress(totalPitchCards: number) {
    const processFromStorage: OrganizationProgress =
      this.storageService.getItem(AppSettings.ORGANIZATION_PROGRESS, true);
    if (processFromStorage) {
      processFromStorage.businessCount = totalPitchCards;
      this.storageService.setItem(
        AppSettings.ORGANIZATION_PROGRESS,
        processFromStorage
      );
    }
    if (this.userService?.userOrganizationProgress?.length) {
      this.userService.userOrganizationProgress[0].businessCount =
        totalPitchCards;
    }
  }
}
